import React, { useState } from 'react'
import PropTypes from 'prop-types'
import update from 'immutability-helper'
import { Button, Modal, Select } from '@resurs/midas-react'
import styles from '../News.module.scss'
import { getText } from '../../../internal/TextLabels'
import NewsFormStore from './NewsFormStore'
import NewsFormRepresentative from './NewsFormRepresentative'
import NewsFormCountry from './NewsFormCountry'
import NewsFormRole from './NewsFormRole'

const EditNewsTarget = ({
  title,
  newsStores,
  newsRepresentatives,
  newsCountries,
  newsRoles,
  target,
  roleTarget,
  representatives,
  stores,
  getRepresentativeStores,
  onBack,
  onSave,
}) => {
  const [state, setState] = useState({
    tabIndex: 0,
    newsStores: [...newsStores],
    newsRepresentatives,
    newsCountries,
    newsRoles,
    target,
    roleTarget,
  })

  const [isButtonDisabled, setButtonDisabled] = useState(true)

  const onRolesChanged = values => {
    setState({
      ...state,
      newsRoles: values.map(id => ({ id })),
    })
    setButtonDisabled(false)
  }

  const onCountriesChanged = values => {
    const next = []

    values.forEach(value => {
      next.push({ id: value })
    })

    setState({
      ...state,
      newsCountries: next,
      newsRepresentatives: [],
      newsStores: [],
    })
    setButtonDisabled(false)
  }

  const onRepresentativesChanged = next => {
    setState({
      ...state,
      newsRepresentatives: [...next],
      newsStores: [],
      newsCountries: [],
    })
    setButtonDisabled(false)
  }

  const onStoresChanged = next => {
    setState({
      ...state,
      newsStores: [...next],
      newsCountries: [],
      newsRepresentatives: [],
    })
    setButtonDisabled(false)
  }

  return (
    <Modal
      title={title}
      onClose={onBack}
      show
      hideClose
      actions={
        <div className={styles.buttonDiv}>
          <Button type="link" onClick={onBack}>
            {getText('default.close')}
          </Button>
          <Button
            type="primary"
            disabled={isButtonDisabled}
            onClick={() => onSave(state)}
          >
            {getText('default.save')}
          </Button>
        </div>
      }
    >
      <div className={styles.selectStoreRoleDiv}>
        <div>
          <span className={styles.title}>
            {getText('news.add.recipientSection.storesRepresentatives')}
          </span>
          <div>
            <p>
              {getText('news.add.recipientSection.storesRepresentativesDesc')}
            </p>
            <Select
              id="newsTargets"
              value={state.target}
              optionsLabel={getText('news.add.recipientSection.target')}
              values={[
                {
                  key: 'all',
                  value: 'all',
                  valueLabel: getText('news.add.recipientSection.all'),
                },
                {
                  key: 'countries',
                  value: 'countries',
                  valueLabel: getText('news.add.recipientSection.countries'),
                },
                {
                  key: 'representatives',
                  value: 'representatives',
                  valueLabel: getText(
                    'news.add.recipientSection.representatives',
                  ),
                },
                {
                  key: 'stores',
                  value: 'stores',
                  valueLabel: getText('news.add.recipientSection.stores'),
                },
              ]}
              onChange={value => {
                setState(
                  update(state, {
                    target: { $set: value },
                  }),
                )
                setButtonDisabled(false)
              }}
            />
            {state.target === 'countries' && (
              <NewsFormCountry
                fieldName="newsCountries"
                fieldCaption=""
                newsCountries={state.newsCountries}
                onChange={onCountriesChanged}
              />
            )}
            {state.target === 'representatives' && (
              <NewsFormRepresentative
                fieldName="newsRepresentatives"
                fieldCaption=""
                newsRepresentatives={state.newsRepresentatives}
                representatives={representatives}
                onChange={onRepresentativesChanged}
              />
            )}
            {state.target === 'stores' && (
              <NewsFormStore
                fieldName="newsStores"
                fieldCaption=""
                newsStores={state.newsStores}
                stores={stores}
                representatives={representatives}
                onChange={onStoresChanged}
                getStores={representativeId =>
                  getRepresentativeStores(representativeId)
                }
              />
            )}
          </div>
        </div>
        <div>
          <span className={styles.title}>
            {getText('news.add.recipientSection.roles')}
          </span>
          <p>{getText('news.add.recipientSection.rolesDesc')}</p>
          <Select
            id="newsRolesFilter"
            optionsLabel={getText('news.add.recipientSection.roles')}
            value={state.roleTarget}
            values={[
              {
                key: 'all',
                value: 'all',
                valueLabel: getText('news.add.recipientSection.all'),
              },
              {
                key: 'specific-roles',
                value: 'specific-roles',
                valueLabel: getText('news.add.recipientSection.specificRoles'),
              },
            ]}
            onChange={value => {
              setState(
                update(state, {
                  roleTarget: { $set: value },
                }),
              )
              setButtonDisabled(false)
            }}
          />
          {state.roleTarget === 'specific-roles' && (
            <NewsFormRole
              fieldName="newsRoles"
              fieldCaption=""
              newsRoles={state.newsRoles}
              onChange={onRolesChanged}
            />
          )}
        </div>
      </div>
    </Modal>
  )
}

EditNewsTarget.propTypes = {
  title: PropTypes.string.isRequired,
  target: PropTypes.string.isRequired,
  roleTarget: PropTypes.string.isRequired,
  newsRoles: PropTypes.array.isRequired,
  newsCountries: PropTypes.array.isRequired,
  newsRepresentatives: PropTypes.array.isRequired,
  newsStores: PropTypes.array.isRequired,
  representatives: PropTypes.array.isRequired,
  stores: PropTypes.array.isRequired,
  getRepresentativeStores: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
}

export default EditNewsTarget
